import React, { useRef, useState } from 'react'
import { defaultTo, equals, find, path, pipe, prop, propOr } from 'ramda'
import PropTypes from 'prop-types'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import { getCancelToken, useDeepCompareEffect, useRequest } from 'storfox-api-hooks'

import useDebounce from '~/hooks/useDebounce'

const defaultGetOptionValue = value => {
  if (value) {
    const { id, guid, name } = value
    return { id, guid, name }
  }

  return null
}

const defaultGetOptionLabel = value => {
  if (value) {
    return propOr('', 'name', value)
  }

  return ''
}

const getItemFromResults = (results, id, key) =>
  find(
    pipe(
      prop(key),
      equals(id)
    )
  )(results)

function SearchField (props) {
  const {
    primaryKey,
    api,
    disabled,
    value,
    onChange,
    renderOption,
    getOptionValue = defaultGetOptionValue,
    getOptionLabel,
    disableClearable,
    PaperComponent,
    ListboxComponent,
    InputProps,
    onInputChange,
    disableCloseOnSelect,
    onValueChange,
    searchFieldProps,
    isLoading,
    size = 'small',
    variant = 'outlined',
    params = { limit: 30 },
    fullWidth = true,
    ListboxProps,
    ...defaultProps
  } = props

  const request = useRequest()
  const [inputValue, setInputValue] = useState('')
  const debouncedValue = useDebounce(inputValue)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const ref = useRef([])

  useDeepCompareEffect(() => {
    let active = true

    const { token, cancel } = getCancelToken()

    if (open) {
      setLoading(true)
      onInputChange(debouncedValue)
      const searchValue = value ? '' : debouncedValue
      request.get(api, { ...params, search: searchValue }, { cancelToken: token })
        .then(response => {
          const results = defaultTo([], path(['data', 'results'], response))

          if (active) {
            ref.current = results
            setOptions(results.map(getOptionValue))
            setLoading(false)
          }
        })
    }

    return () => {
      active = false
      cancel()
    }
  }, [open, debouncedValue])

  React.useEffect(() => {
    if (!open) {
      ref.current = []
      setOptions([])
    }
  }, [open])

  return (
    <Autocomplete
      open={open}
      disabled={disabled}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect}
      value={value}
      isOptionEqualToValue={(option, value) => option[primaryKey] === value[primaryKey]}
      getOptionLabel={getOptionLabel}
      options={options.length === 0 && !open ? [value] : options}
      loading={loading}
      filterOptions={item => item}
      onChange={(event, value) => {
        if (value) {
          onChange(event, getItemFromResults(ref.current, value[primaryKey], primaryKey))
        } else {
          onChange(event, null)
        }
        onValueChange(value)
      }}
      PaperComponent={PaperComponent}
      ListboxComponent={ListboxComponent}
      ListboxProps={ListboxProps}
      renderOption={renderOption}
      sx={{
        display: 'inline-block',
        minWidth: '200px'
      }}
      onInputChange={(event, value) => {
        setInputValue(value)
        setOptions([])
      }}
      renderInput={params => {
        return (
          <TextField
            {...params}
            {...defaultProps}
            size={size}
            variant={variant}
            fullWidth={fullWidth}
            InputProps={{
              ...params.InputProps,
              ...InputProps,
              endAdornment: (
                <>
                  {(isLoading || loading) ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )
      }}
      {...searchFieldProps}
    />
  )
}

SearchField.propTypes = {
  primaryKey: PropTypes.string.isRequired,
  api: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  size: PropTypes.string.isRequired,
  fullWidth: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  input: PropTypes.object.isRequired,
  renderOption: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  meta: PropTypes.object.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  disableClearable: PropTypes.bool.isRequired,
  InputProps: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  PaperComponent: PropTypes.func,
  ListboxComponent: PropTypes.func,
  ListboxProps: PropTypes.object,
  onValueChange: PropTypes.func,
  disableCloseOnSelect: PropTypes.bool,
  value: PropTypes.string.isRequired,
  searchFieldProps: PropTypes.shape({
    PopperComponent: PropTypes.func
  }),
}

SearchField.defaultProps = {
  primaryKey: 'id',
  disabled: false,
  isLoading: false,
  getOptionValue: defaultGetOptionValue,
  getOptionLabel: defaultGetOptionLabel,
  disableClearable: false,
  onChange: () => {},
  onValueChange: () => {},
  onInputChange: () => {}
}

export default SearchField
